import { message } from 'antd';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import http from '../../http';
import AddPageModal from '../../components/AddPage/AddPage';

const ManifestAddPage = ({
    visible,
    onClose,
    onSuccessSubmit,
    onErrorSubmit,
    edit,
    editItem,
    project,
    duplicate,
    reload,
}) => {
    const [initialValues, setInitialValues] = useState({
        project: project.id,
        machine_type: 1,
        construction_type: 1,
        billing_preference: 'daily',
    });
    const defaultValues = {
        project: project.id,
        machine_type: 1,
        construction_type: 1,
        positioning_method: 0,
        billing_preference: 'daily',
    };
    const [hiddenCompactionCount, setHiddenCompactionCount] = useState(true);
    const [hiddenTileSize, setHiddeTileSize] = useState(true);

    // Log type selection: '' | 'boring' | 'excavation'
    const [guidanceService, setGuidanceService] = useState('');

    const { t } = useTranslation();
    const config = {
        addUrl: !duplicate
            ? '/api/v1/projects/manifests/add/'
            : '/api/v1/projects/manifests/duplicate/',
        editUrl: edit ? `/api/v1/projects/manifests/${editItem?.id}/edit/` : '',
        dataUrl: edit ? `/api/v1/projects/manifests/${editItem?.id}/` : '',
        pageTitle: !edit
            ? !duplicate
                ? t('manifest_addpage_title', { project_name: project.project_name })
                : t('manifest_duplicatepage_title', { project_name: project.project_name })
            : t('addpage_edit_button_text') + ' ' + `${editItem?.name || ''}`,
        colNo: 2,
        cancelButtonText: t('addpage_cancel_button_text'),
        saveButtonText: !edit
            ? !duplicate
                ? t('addpage_save_button_text')
                : t('duplicate_button_text')
            : t('addpage_edit_button_text'),
        addSuccessMessage: !edit
            ? !duplicate
                ? t('manifest_add_successful_text')
                : t('manifest_duplicate_successful_text')
            : t('manifest_edit_successful_text'),
        modalSize: 800,
        fields: [
            {
                name: 'name',
                label: t('manifest_name_field'),
                placeholder: t('manifest_name_field_placeholder'),
                isRequired: true,
                requiredMessage: t('manifest_name_required_text'),
                type: 'input',
                colNo: 1,
                order: 1,
            },
            {
                name: 'construction_type',
                label: t('manifest_constructiontype_field'),
                placeholder: t('manifest_constructiontype_field'),
                isRequired: false,
                requiredMessage: '',
                type: 'select',
                extraProps: {
                    url: '/api/v1/enums/ManifestChoices/combo/',
                    withTranslation: true,
                },
                colNo: 1,
                order: 2,
            },
            {
                name: 'machine_type',
                label: t('manifest_machinetype_field'),
                placeholder: t('manifest_machinetype_field'),
                isRequired: false,
                type: 'select',
                extraProps: {
                    url: '/api/v1/enums/MachineTypeChoices/combo/',
                    withTranslation: true,
                    onSelect: (value, childrens) => {
                        if (value === 2) {
                            setHiddeTileSize(false);
                            setHiddenCompactionCount(false);
                        } else {
                            setHiddeTileSize(true);
                            setHiddenCompactionCount(true);
                        }
                    },
                    disabled: edit,
                },
                requiredMessage: '',
                colNo: 1,
                order: 3,
            },
            {
                name: 'positioning_method',
                label: t('manifest_positioningmethod_field'),
                placeholder: t('manifest_positioningmethod_field_placeholder'),
                isRequired: false,
                type: 'select',
                extraProps: {
                    url: '/api/v1/enums/ManifestPositionMethodChoices/combo/',
                },
                requiredMessage: '',
                colNo: 1,
                order: 4,
            },
            {
                name: 'guidance_service',
                label: t('manifest_log_type_field', 'Additional Guidance Service'),
                placeholder: t('manifest_log_type_field_placeholder', 'Select Additional Guidance Service'),
                isRequired: false,
                requiredMessage: '',
                type: 'select',
                extraProps: {
                    data: [
                        {
                            id: 'excavation',
                            name: t('manifest_excavation_log_field', 'Excavation Record'),
                        },
                        { id: 'boring', name: t('manifest_boring_log_field', 'Boring Guidance') },
                    ],
                    onChange: (value) => setGuidanceService(value || ''),
                },
                colNo: 1,
                order: 4.1,
            },
            {
                name: 'billing_preference',
                label: t('manifest_billing_field', 'Billing Preference'),
                placeholder: t('manifest_billing_field_placeholder', 'Select Billing Preference'),
                isRequired: false,
                requiredMessage: '',
                type: 'select',
                hidden: !guidanceService,
                extraProps: {
                    // Not clearable: a guidance service always needs a billing preference
                    allowClear: false,
                    data: [
                        { id: 'hourly', name: 'Hourly' },
                        { id: 'daily', name: 'Daily' },
                        { id: 'full_period', name: 'Machine Schedule Period' },
                    ],
                },
                colNo: 1,
                order: 4.2,
            },
            {
                name: 'compaction_count',
                label: t('manifest_compactioncount_field'),
                placeholder: t('manifest_compactioncount_field'),
                isRequired: false,
                requiredMessage: '',
                colNo: 1,
                order: 5,
                hidden: hiddenCompactionCount,
            },
            {
                name: 'tile_size',
                label: t('manifest_tile_size_field'),
                placeholder: t('manifest_tile_size_field'),
                isRequired: false,
                requiredMessage: '',
                colNo: 1,
                order: 5,
                hidden: hiddenTileSize,
            },
            {
                name: 'reference',
                label: t('manifest_reference_field'),
                placeholder: t('manifest_reference_field_placeholder'),
                isRequired: false,
                requiredMessage: 'Reference Point File Needed',
                type: 'file',
                colNo: 2,
                order: 1,
            },
            {
                name: 'localization',
                label: t('manifest_localization_field'),
                placeholder: t('manifest_localization_field_placeholder'),
                isRequired: false,
                requiredMessage: 'Localization File Required',
                type: 'file',
                colNo: 2,
                order: 2,
            },
            {
                name: 'site_data_2d',
                label: t('manifest_dxf_field'),
                placeholder: t('manifest_dxf_file_placeholder'),
                isRequired: false,
                requiredMessage: '2D Site Data Required',
                type: 'file',
                colNo: 2,
                order: 3,
            },
            {
                name: 'site_data',
                label: t('manifest_xml_fields'),
                placeholder: t('manifest_xml_fields'),
                isRequired: false,
                requiredMessage: '3D Site Data Required',
                type: 'file',
                colNo: 2,
                order: 4,
            },
            {
                name: 'site_data_sub',
                label: t('manifest_sub_xml_field'),
                placeholder: t('manifest_sub_xml_field'),
                isRequired: false,
                requiredMessage: '3D Site Data Sub Required',
                type: 'file',
                colNo: 2,
                order: 5,
            },
        ],
    };

    const onFinish = async (values, handleError, form) => {
        const formData = new FormData();
        formData.append('project', project.id);
        const requestConfig = {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        };

        // log_type + billing_preference now sent flat (backend ManifestSerializer
        // accepts them directly and syncs the single service preference).
        for (let name in values) {
            const value = values[name];

            if (value instanceof File) {
                formData.append(name, value, value.name);
            }
            // Append other values to the FormData object
            else if (value !== 0 && (!value || value === 'undefined' || value === 'null')) {
                formData.append(name, '');
            } else {
                formData.append(name, value);
            }
        }

        formData.set('guidance_service', values.guidance_service || '');
        formData.set(
            'billing_preference',
            values.guidance_service ? values.billing_preference || 'daily' : ''
        );

        if (!edit) {
            if (duplicate && editItem) {
                formData.append('original_id', editItem.id);
            }
            try {
                const resp = await http.post(config.addUrl, formData, requestConfig);
                if (resp.status === 201) {
                    message.success(
                        config.addSuccessMessage || resp.data.message || 'Added Successfully');
                    if (onSuccessSubmit) {
                        onSuccessSubmit();
                        form.resetFields();
                        form.setFieldsValue(defaultValues);
                        setGuidanceService('');
                    } else if (onClose) {
                        onClose();
                        form.resetFields();
                        form.setFieldsValue(defaultValues);
                        setGuidanceService('');
                    }
                }
            } catch (error) {
                handleError(error);
                if (onErrorSubmit) {
                    onErrorSubmit();
                }
            }
        } else {
            try {
                const response = await http.patch(config.editUrl, formData);
                if (response.status === 200) {
                    message.success(
                        config.addSuccessMessage || response.data.message || 'Added Successfully');
                    if (onSuccessSubmit) {
                        onSuccessSubmit();
                        form.resetFields();
                        form.setFieldsValue(defaultValues);
                        setGuidanceService('');
                    } else if (onClose) {
                        onClose();
                        form.resetFields();
                        form.setFieldsValue(defaultValues);
                        setGuidanceService('');
                    }
                }
            } catch (error_1) {
                handleError(error_1);
                if (onErrorSubmit) {
                    onErrorSubmit();
                }
            }
        }
    };

    useEffect(() => {
        setInitialValues({});
        if (editItem) {
            let dataUrl = `/api/v1/projects/manifests/${editItem?.id}/`;
            http.get(dataUrl)
                .then((resp) => {
                    let data = { ...resp.data }; // Clone data for modification
                    if (duplicate) {
                        data.name = data.name + ' Copy';
                        data.project = project.id;
                    }
                    data.localization = data.localization_file_name;
                    data.reference = data.reference_file_name;
                    data.site_data = data.site_data_file_name;
                    data.site_data_sub = data.site_data_sub_file_name;
                    data.site_data_2d = data.site_data_2d_file_name;

                    const pref = data.service_preferences?.find((p) => p.is_active);
                    if (pref) {
                        setGuidanceService(pref.service_type);
                        data.guidance_service = pref.service_type;
                        data.billing_preference = pref.billing_preference || 'daily';
                    } else {
                        setGuidanceService('');
                        data.guidance_service = null;
                        data.billing_preference = 'daily';
                    }

                    setInitialValues(data);
                    if (data.machine_type === 2) {
                        setHiddeTileSize(false);
                        setHiddenCompactionCount(false);
                    } else {
                        setHiddeTileSize(true);
                        setHiddenCompactionCount(true);
                    }
                })
                .catch((err) => {
                    message.error('Loading Data Failed');
                });
        }
    }, [editItem]);

    return (
        <AddPageModal
            onFinish={onFinish}
            config={config}
            visible={visible}
            onClose={onClose}
            onSuccessSubmit={onSuccessSubmit}
            onErrorSubmit={onErrorSubmit}
            edit={edit}
            initialValues={initialValues}
            defaultValues={defaultValues}
        />
    );
};

export default ManifestAddPage;
